<template>
  <el-card v-loading="loading">
    <el-form :model="dataForm" :rules="dataRule" ref="dataForm" label-position="top">
      <el-row :gutter="20">
        <el-col :span="24">
          <copy-from
            :dataForm="dataForm"
            :options="serviceList"
            :labelFormatter="item => `[${item.serviceCode}]${item.description}`"
            @copyFromTrigger="copyFromTrigger"
          />
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="Code" prop="serviceCode">
            <el-input v-model="dataForm.serviceCode" clearable placeholder="SG-N1" v-regexFilter="halfWidthRegex"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="Country" prop="country">
            <el-select v-model="dataForm.country" filterable clearable placeholder="Country" class="wdb100" @change="countryChange">
              <el-option v-for="item in countryList" :key="item.value" :label="item.title" :value="item.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item prop="businessTypeList">
            <span slot="label">
              Business Type
              <el-popover trigger="hover" content="Select country first">
                <i slot="reference" class="el-icon-info"></i>
              </el-popover>
            </span>
            <el-select v-model="dataForm.businessTypeList" multiple clearable placeholder="Select Business Type" class="fluid-width">
              <el-option v-for="item in supportBusinessTypesOptions" :key="item.value" :label="item.title" :value="item.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item prop="supplierId">
            <span slot="label">
              Supplier
              <el-popover trigger="hover" content="Select country first">
                <i slot="reference" class="el-icon-info"></i>
              </el-popover>
            </span>
            <el-select v-model="dataForm.supplierId" clearable placeholder="Select Supplier" class="fluid-width">
              <el-option v-for="item in supplierList" :key="item.id" :label="item.companyName" :value="item.id"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="Description (EN)" prop="description">
            <el-input v-model="dataForm.description" clearable placeholder="Description (EN)" v-regexFilter="halfWidthRegex"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Description (CN)" prop="descriptionCn">
            <el-input v-model="dataForm.descriptionCn" clearable placeholder="Description (CN)"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Description (Local)" prop="descriptionLocal">
            <el-input v-model="dataForm.descriptionLocal" clearable placeholder="Description (Local)"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="Service Scope (EN)" prop="serviceScope">
            <el-input
              v-model="dataForm.serviceScope"
              type="textarea"
              :rows="3"
              resize="none"
              clearable
              placeholder="Service Scope (EN)"
              v-regexFilter="halfWidthRegex"
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Service Scope (CN)" prop="serviceScopeCn">
            <el-input v-model="dataForm.serviceScopeCn" type="textarea" :rows="3" resize="none" clearable placeholder="Service Scope (CN)"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="4">
          <el-form-item label="Currency" prop="currency">
            <el-select v-model="dataForm.currency" filterable clearable placeholder="Select Currency" class="wdb100">
              <el-option v-for="item in currencyList" :key="item.currency" :label="item.currency" :value="item.currency"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item label="Price" prop="serviceFee">
            <el-input v-model="dataForm.serviceFee" v-positivePoint placeholder="0.00">
              <template slot="prefix">{{ currencySymbolFormat(dataForm.currency) }}</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item label="Original Price" prop="originalServiceFee">
            <el-input v-model="dataForm.originalServiceFee" v-positivePoint placeholder="0.00">
              <template slot="prefix">{{ currencySymbolFormat(dataForm.currency) }}</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item
            label="Third Party Fee"
            prop="thirdPartyFee"
            :rules="{
              required: dataForm.thirdPartyFeeDescription ? true : false,
              message: 'please enter',
              trigger: 'submit',
            }"
          >
            <el-input v-model="dataForm.thirdPartyFee" v-positivePoint placeholder="0.00">
              <template slot="prefix">{{ currencySymbolFormat(dataForm.currency) }}</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item
            label="Other Fee"
            prop="otherFee"
            :rules="{
              required: dataForm.otherFeeDescription ? true : false,
              message: 'please enter',
              trigger: 'submit',
            }"
          >
            <el-input v-model="dataForm.otherFee" v-positivePoint placeholder="0.00">
              <template slot="prefix">{{ currencySymbolFormat(dataForm.currency) }}</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item
            label="Note For Third Party Fee (EN)"
            prop="thirdPartyFeeDescription"
            :rules="{
              required: $isNumberLike(dataForm.thirdPartyFee) ? true : false,
              message: 'please enter',
              trigger: 'submit',
            }"
          >
            <el-input v-model="dataForm.thirdPartyFeeDescription" clearable placeholder="EN"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Note For Third Party Fee (CN)" prop="thirdPartyFeeDescriptionCn">
            <el-input v-model="dataForm.thirdPartyFeeDescriptionCn" clearable placeholder="CN"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Note For Third Party Fee (Local)" prop="thirdPartyFeeDescriptionLocal">
            <el-input v-model="dataForm.thirdPartyFeeDescriptionLocal" clearable placeholder="Local"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item
            label="Note For Other Fee (EN)"
            prop="otherFeeDescription"
            :rules="{
              required: $isNumberLike(dataForm.otherFee) ? true : false,
              message: 'please enter',
              trigger: 'submit',
            }"
          >
            <el-input v-model="dataForm.otherFeeDescription" clearable placeholder="EN"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Note For Other Fee (CN)" prop="otherFeeDescriptionCn">
            <el-input v-model="dataForm.otherFeeDescriptionCn" clearable placeholder="CN"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Note For Other Fee (Local)" prop="otherFeeDescriptionLocal">
            <el-input v-model="dataForm.otherFeeDescriptionLocal" clearable placeholder="Local"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="4">
          <el-form-item label="Sort No." prop="orderNum">
            <el-input v-model="dataForm.orderNum" clearable placeholder="Order Number" v-positiveInteger></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <el-form-item label="Status" prop="status">
            <el-radio-group v-model="dataForm.status">
              <el-radio :label="0">Disable</el-radio>
              <el-radio :label="1">Enable</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
      </el-row>
      <div class="pt16">
        <el-button type="primary" :loading="submitLoading" @click="dataFormSubmit">Submit</el-button>
        <el-button @click="() => $router.replace('/sys/pkg/module')">Back</el-button>
      </div>
    </el-form>
  </el-card>
</template>

<script>
import CopyFrom from "@/components/CopyFrom";
import formatterMixin from "@/mixins/formatterMixin";
import { mapState } from "vuex";
export default {
  mixins: [formatterMixin],
  components: {
    CopyFrom,
  },
  data() {
    return {
      dataId: null,
      loading: false,
      submitLoading: false,
      dataForm: {
        orderNum: "",
        serviceCode: "",
        supplierId: "",
        country: "",
        description: "",
        descriptionCn: "",
        descriptionLocal: "",
        serviceScope: "",
        serviceScopeCn: "",
        thirdPartyFeeDescription: "",
        thirdPartyFeeDescriptionCn: "",
        thirdPartyFeeDescriptionLocal: "",
        otherFeeDescription: "",
        otherFeeDescriptionCn: "",
        otherFeeDescriptionLocal: "",
        businessTypeList: [],
        currency: "",
        serviceFee: "",
        originalServiceFee: "",
        thirdPartyFee: "",
        otherFee: "",
        status: 1,
      },
      dataRule: {
        serviceCode: [{ required: true, message: "please enter", trigger: "submit" }],
        businessTypeList: [{ type: "array", required: true, message: "please select", trigger: "submit" }],
        description: [{ required: true, message: "please enter", trigger: "submit" }],
        descriptionCn: [{ required: true, message: "please enter", trigger: "submit" }],
        serviceFee: [{ required: true, message: "please enter", trigger: "submit" }],
        currency: [{ required: true, message: "please select", trigger: "submit" }],
        country: [{ required: true, message: "please select", trigger: "submit" }],
        supplierId: [{ required: true, message: "please select", trigger: "submit" }],
      },
      serviceList: [],
      supplierList: [],
      allSupplierList: [],
      supportBusinessTypesOptions: [],
    };
  },
  computed: {
    ...mapState("constant", ["countryList"]),
    ...mapState("currency", ["currencyList"]),
  },
  methods: {
    // 获取服务列表
    getServiceList() {
      return new Promise((resolve, reject) => {
        this.$http
          .get("/admin/service/list")
          .then(res => {
            this.serviceList = res.data || [];
            resolve();
          })
          .catch(err => {
            reject();
          });
      });
    },
    // 获取供应商列表
    getSupplierList() {
      return new Promise((resolve, reject) => {
        this.$http
          .get("/admin/supplier")
          .then(res => {
            this.allSupplierList = res.data || [];
            resolve();
          })
          .catch(err => {
            reject();
          });
      });
    },
    // 获取详情
    getDetail() {
      return new Promise((resolve, reject) => {
        this.$http
          .get(`/admin/service/detail/${this.dataId}`)
          .then(res => {
            const resData = { ...res.data };
            this.countryChange(resData.country, false);
            resData.supplierId = resData.supplier?.id || "";
            this.dataForm = resData;
            resolve();
          })
          .catch(err => {
            reject();
          });
      });
    },
    // 提交表单
    async dataFormSubmit() {
      this.$refs["dataForm"].validate(async valid => {
        if (valid) {
          this.submitLoading = true;
          const dataId = this.dataId;
          const dataForm = { ...this.dataForm };
          this.$http
            .post(dataId ? `/admin/service/update/${dataId}` : "/admin/service/create", dataForm)
            .then(res => {
              this.$message({
                message: "success",
                type: "success",
                duration: 1500,
                onClose: () => {
                  this.$router.replace("/sys/pkg/module");
                },
              });
            })
            .finally(() => {
              this.submitLoading = false;
            });
        }
      });
    },
    // country选中时触发
    countryChange(val, isSupplierEmpty = true) {
      // 供应商列表
      const list = this.allSupplierList.filter(item => item.country == val);
      this.supplierList = list || [];
      // 支持的服务列表
      const currentCountry = this.countryList.find(ele => ele.value == val);
      this.supportBusinessTypesOptions = currentCountry?.supportBusinessTypesList || [];
      // 是否重置选中值
      if (isSupplierEmpty) {
        this.$set(this.dataForm, "supplierId", "");
        this.$set(this.dataForm, "businessTypeList", []);
      }
    },
    // 接收复制完成的数据
    copyFromTrigger(originalData, newDataForm) {
      this.countryChange(originalData.country, false);
      newDataForm.supplierId = originalData?.supplier?.id || "";
      this.dataForm = newDataForm;
    },
  },
  async activated() {
    this.loading = true;
    this.submitLoading = false;
    this.$refs.dataForm?.resetFields();
    this.dataId = this.$route.params?.id || "";
    try {
      await this.getSupplierList();
      if (this.dataId) await this.getDetail();
      await this.getServiceList();
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .el-form-item {
    margin-bottom: 20px !important;

    .el-form-item__label {
      padding: 0px !important;
      line-height: 30px !important;
    }

    .el-form-item__error {
      padding-top: 0px !important;
    }
  }

  .el-input--prefix {
    .el-input__inner {
      padding-left: 40px !important;
    }
  }
}
</style>
